/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import { Paper, SxProps, TextField, Theme } from '@mui/material'
import Autocomplete, { AutocompleteChangeReason, AutocompleteInputChangeReason, AutocompleteRenderInputParams, createFilterOptions } from '@mui/material/Autocomplete'

/** ICON */
import { faCaretDown, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

/** CONSTANTS */
import { colors } from 'constants/theme'

/** STYLE */
import { styled } from '@mui/material/styles'
import HelperText from 'component/Input/HelperText'
import 'component/Select/Style.css'
import { useMemo } from 'react'

const CustomAutocomplete = styled(Autocomplete)(({ theme }) => ({
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
        // padding: '8px 8px 8px 8px',
    },
    '.MuiOutlinedInput-root': {
        backgroundColor: colors.white,
        height: 43,
        alignItems: 'center !important',
        paddingRight: '36px !important',
        '.MuiButtonBase-root': {
            '&.MuiIconButton-root': {
                backgroundColor: `${colors.transparent} !important`,
            },
            svg: {
                fontSize: 16,
                path: {
                    fill: colors.black
                }
            }
        },
        '&.Mui-disabled': {
            backgroundColor: `#F0F0F0 !important`,
            '.MuiButtonBase-root svg': {
                path: {
                    fill: colors.black60
                }
            },
        },
        '&.MuiInputBase-sizeSmall': {
            '.MuiAutocomplete-input': {
                paddingRight: '16px'
            }
        },
        '&.Mui-focused': {
            '.MuiOutlinedInput-notchedOutline': {
                borderColor: `${colors.themeSecondColor}`,
            }
        },
        '.MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px !important'
        }
    },
    '.MuiInputLabel-root, .MuiInputLabel-root span': {
        fontSize: 16,
        color: `${colors.black60} !important`,
        backgroundColor: 'transparent !important',
        '&:before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: 3,
            top: '50%',
            zIndex: -1
        },
        '&[data-shrink="true"]': {
            '&:before': {
                backgroundColor: colors.white
            },
            span: {
                color: `${colors.statusInactive} !important`
            }
        }
    },
    '.MuiOutlinedInput-notchedOutline': {
        borderColor: `${colors.black12} !important`
    },
    '.MuiOutlinedInput-input': {
        textIndent: '1px',
        color: `${colors.textPrimary} !important`,
        '-webkit-text-fill-color': 'unset !important',
        '&.Mui-disabled': {
            '-webkit-text-fill-color': 'unset',
            color: `${colors.black60} !important`,
            '&::placeholder': {
                opacity: 1
            }
        }
    },
    '.MuiAutocomplete-clearIndicator': {
        backgroundColor: 'transparent !important',
        svg: {
            fontSize: 20
        },
        path: {
            fill: `${colors.black60} !important`
        }
    },
    'span.MuiAutocomplete-tag': {
        height: 23,
        minWidth: 36,
        maxWidth: '50%',
        backgroundColor: colors.disabledGray,
        borderRadius: 100,
        color: colors.white,
        textAlign: 'center',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    '&.Mui-enabled-border': {
        '.MuiOutlinedInput-root': {
            '&.Mui-focused': {
                '.MuiOutlinedInput-notchedOutline': {
                    borderColor: `${colors.themeSecondColor} !important`
                }
            }
        },
        '.MuiInputLabel-root, .MuiInputLabel-root span': {
            '&[data-shrink="true"]': {
                '&.Mui-focused': {
                    color: `${colors.themeSecondColor} !important`
                }
            }
        }
    },
    '&.Mui-error': {
        '&.Mui-enabled-border, &.Mui-disabled-border, .MuiOutlinedInput-root.Mui-focused': {
            '.MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${colors.statusInactive} !important`
            },
            '.MuiInputLabel-root[data-shrink="true"], .MuiInputLabel-root[data-shrink="true"] span': {
                color: `${colors.statusInactive} !important`
            }
        }
    },
    '.MuiInputBase-inputMultiline': {
        height: '90% !important'
    },
    '&.Mui-multiple': {
        '.MuiOutlinedInput-root': {
            paddingTop: '10px !important'
        },
        '.MuiAutocomplete-clearIndicator': {
            marginRight: '-2px !important'
        }
    },
    '& .MuiAutocomplete-noOptions': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
        padding: theme.spacing(1, 0)
    }
}))

type SelectSearch = {
    labelId: string
    inputLabel?: string
    options: any
    size?: 'medium' | 'small'
    defaultValue?: any
    noOptionsText?: string
    disableClearable?: boolean
    popupIcon?: any
    clearIcon?: any
    open?: boolean
    disabled?: boolean
    renderOption?: (props: any, option: any, selected?: any) => any
    renderTags?: (props: any, option: any, owner?: any) => any
    getOptionLabel?: (option: any) => any
    getOptionDisabled?: (option: any) => any
    filterOptions?: (option: any) => any
    onchange?: (event: any, value: any, reason: AutocompleteChangeReason, details?: any) => void
    onInputChange?: (event: any, value: any, reason?: AutocompleteInputChangeReason) => void
    onOpen?: (event: any, value: any) => void
    onClose?: (event: any, reason: any) => void
    onBlur?: (event: any) => void
    value?: any
    disabledBorder?: boolean
    required?: boolean
    errMessage?: string
    multiple?: boolean
    multiline?: boolean
    freeSolo?: boolean
    height?: number | string
    limitTags?: number
    classesOption?: string
    placeholder?: string
    inputValue?: any
    isOptionEqualToValue?: any
    inputProps?: any
    loading?: boolean
    loadingText?: any
    clearOnBlur?: boolean
    clearOnEscape?: boolean
    isDisabled?: boolean
    params?: any
    maxHight?: number | string
    getInputSearch?: (props: AutocompleteRenderInputParams) => void
    ariaSelectedSx?: any
    sxPaper?: any
    sxAutoComplete?: SxProps<Theme>
}

export default function AutocompleteSelect(props: SelectSearch) {
    const { t } = useTranslation()

    const filterOptions = useMemo(() => createFilterOptions({
        stringify: props.filterOptions
    }), [props.filterOptions]);

    const listboxProps = useMemo(() => ({
        style: { maxHeight: props.maxHight ? props.maxHight : '' }
    }), [props.maxHight]);
    const noOptionsMsg = props.noOptionsText ?? t('REGISTER.FORM.NOT_FOUND')
    const hasValue = props.multiple
        ? Array.isArray(props.value) && props.value.length > 0
        : Boolean(props.value);
    return (
        <>
            <CustomAutocomplete
                id={props.labelId}
                open={props.open}
                ListboxProps={listboxProps}
                options={props.options}
                renderOption={props.renderOption}
                renderTags={props.renderTags}
                getOptionLabel={props.getOptionLabel}
                getOptionDisabled={props.getOptionDisabled}
                renderInput={(params) => {
                    if (props?.getInputSearch) props.getInputSearch(params)
                    return (
                        <TextField
                            {...params}
                            {...props.inputProps}
                            label={props.inputLabel}
                            placeholder={props.placeholder}
                            required={props.required}
                            multiline={props.multiline}
                            renderInput={(params: any) => {
                                return (
                                    <TextField
                                        {...params}
                                        label={props.inputLabel}
                                        placeholder={props.placeholder}
                                        required={props.required}
                                        multiline={props.multiline}
                                        inputProps={{
                                            ...params.inputProps,
                                            ...props.inputProps
                                        }}
                                    />
                                )
                            }}
                        />
                    )
                }}
                onChange={props.onchange}
                onInputChange={props.onInputChange}
                onOpen={props.onOpen}
                onClose={props.onClose}
                onBlur={props.onBlur}
                disablePortal
                limitTags={props.limitTags}
                size={props.size || 'small'}
                popupIcon={props.popupIcon || <FontAwesomeIcon icon={faCaretDown} />}
                clearIcon={props.clearIcon || <FontAwesomeIcon icon={faTimes} />}
                classes={{ popper: `style-filter-select-search-wrap ${props.classesOption}`, paper: 'style-filter-select-search', listbox: props.multiple ? 'Mui-multiple custom-scroll' : 'custom-scroll' }}
                sx={{
                    '.MuiOutlinedInput-root': {
                        paddingTop: props.multiple ? '10px !important' : props.height === 32 ? '2px !important' : '',
                        minHeight: props.multiple ? props.height : 'unset',
                        height: (props.multiple && 'auto') || (props.height) || 43,
                        alignItems: props.multiple ? 'start !important' : 'center  !important',
                    },
                    ...props?.sxAutoComplete
                }}
                className={`${props.disabledBorder ? 'Mui-disabled-border' : 'Mui-enabled-border'} ${props.errMessage ? 'Mui-error' : ''} ${props.multiple ? 'Mui-multiple' : ''}`}
                filterOptions={createFilterOptions({
                    stringify: props.filterOptions
                })}
                //filterOptions={filterOptions}
                noOptionsText={noOptionsMsg}
                defaultValue={props.defaultValue}
                disableClearable={props.disableClearable || !hasValue}
                inputValue={props.inputValue}
                value={props.value}
                disabled={props.disabled}
                multiple={props.multiple}
                freeSolo={props.freeSolo}
                isOptionEqualToValue={props.isOptionEqualToValue}
                loading={props.loading}
                loadingText={t('SEARCHING')}
                clearOnBlur={props.clearOnBlur}
                clearOnEscape={props.clearOnEscape}
                blurOnSelect
                disableCloseOnSelect={props.multiple ? true : (props.disableClearable || !hasValue)}
                PaperComponent={({ children }) => (
                    <Paper sx={{
                        ...props?.sxPaper,
                        '.MuiInputBase-input': {
                            height: `10px !important`,
                        },
                        '.MuiAutocomplete-option': {
                            '&:hover, &:hover.Mui-focused': {
                                backgroundColor: `${colors.themeSecondColor06} !important`,
                                color: colors.themeSecondColor
                            },
                            '&[aria-selected="true"], &.Mui-focused': {
                                backgroundColor: `${colors.transparent} !important`,
                                ...props?.ariaSelectedSx
                            }
                        }
                    }}>{children}</Paper >
                )
                }
                {...props?.params}
            />
            {
                props.errMessage && (
                    <HelperText label={props.errMessage} />
                )
            }
        </>
    )
}
